const URL_ = "/api";
const STORAGE_KEY = "revisapro.session.v2";

try {
  if (localStorage.getItem("revisapro.auth")) localStorage.removeItem("revisapro.auth");
} catch {}

function getSession() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}
function setSession(session) {
  if (session) localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  else localStorage.removeItem(STORAGE_KEY);
}

function authHeaders() {
  const session = getSession();
  return {
    "Content-Type": "application/json",
    Accept: "application/json",
    ...(session?.token ? { Authorization: `Bearer ${session.token}` } : {})
  };
}

async function doFetch(url, options) {
  const res = await fetch(url, options);
  let body;
  try { body = await res.json(); } catch { body = null; }
  if (!res.ok) {
    let msg = "Erro desconhecido";
    if (typeof body?.detail === "string") {
      msg = body.detail;
    } else if (Array.isArray(body?.detail)) {
      msg = body.detail.map(d => (typeof d === 'string' ? d : (d.msg || d.message || JSON.stringify(d)))).join(", ");
    } else if (body?.detail && typeof body.detail === 'object') {
      msg = body.detail.msg || body.detail.message || JSON.stringify(body.detail);
    } else if (body?.message) {
      msg = body.message;
    } else {
      msg = `Erro HTTP ${res.status}`;
    }
    if (typeof msg !== 'string') msg = JSON.stringify(msg);
    throw new Error(msg);
  }
  return body;
}

export const auth = {
  async signUp(email, password, meta) {
    const res = await doFetch(`${URL_}/auth/register`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password, name: meta?.name || email.split("@")[0] })
    });
    setSession(res);
    return res;
  },
  async signIn(email, password) {
    const res = await doFetch(`${URL_}/auth/login`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password })
    });
    setSession(res);
    return res;
  },
  async signOut() {
    setSession(null);
  },
  async getUser() {
    const session = getSession();
    if (!session?.token) return null;
    try {
      const user = await doFetch(`${URL_}/auth/me`, { method: "GET", headers: authHeaders() });
      return user;
    } catch {
      setSession(null);
      return null;
    }
  },
  getLocalUser() {
    return getSession()?.user || null;
  }
};

const TABLE_MAP = {
  "leads": "leads",
  "projetos": "projects",
  "financeiro": "invoices",
  "propostas": "proposals"
};

// Converte status do frontend (espaços) para o backend (underscores)
const STATUS_TO_BACKEND = {
  "em andamento": "em_andamento",
  "em revisão": "em_revisao",
  "em revisao": "em_revisao",
  "pendente": "pendente",
  "concluido": "entregue",
  "concluído": "entregue",
  "pausado": "pendente",
  "cancelado": "cancelado",
};

// Converte status do backend (underscores) para o frontend (espaços)
const STATUS_TO_FRONTEND = {
  "em_andamento": "em andamento",
  "em_revisao": "pausado",
  "pendente": "pausado",
  "entregue": "concluido",
  "cancelado": "cancelado",
};

function parseCurrency(val) {
  if (typeof val === 'number') return val;
  if (!val) return 0;
  const s = String(val);
  if (s.includes(',')) {
    return Number(s.replace(/\./g, '').replace(',', '.')) || 0;
  }
  return Number(s) || 0;
}

function mapToBackend(table, row) {
  if (table === "leads") return { name: row.nome || "", email: row.email || "", phone: row.telefone || "", status: row.status || "novo", value: parseCurrency(row.valor_estimado), source: row.origem || "", notes: row.notas || "" };
  if (table === "projetos") {
    const rawStatus = row.status || "em andamento";
    const backendStatus = STATUS_TO_BACKEND[rawStatus] || "em_andamento";
    // Garantir que o prazo esteja no formato ISO completo
    let deadline = row.prazo || new Date().toISOString();
    if (deadline && deadline.length === 10) deadline = deadline + "T00:00:00.000Z";
    const mapped = { title: row.cliente ? `${row.cliente} - ${row.servico || 'Projeto'}` : 'Novo Projeto', client_name: row.cliente || "", work_type: row.servico || row.tipo || "", deadline: deadline, price: parseCurrency(row.valor), status: backendStatus, notes: row.notas || "", tasks: row.tasks || [] };
    if (row.revisor_id !== undefined) mapped.revisor_id = row.revisor_id === "nenhum" ? "" : row.revisor_id;
    if (row.file_url !== undefined) mapped.file_url = row.file_url;
    if (row.delivery_url !== undefined) mapped.delivery_url = row.delivery_url;
    if (row.revisor_comments !== undefined) mapped.revisor_comments = row.revisor_comments;
    return mapped;
  }
  if (table === "financeiro") return { client_name: row.cliente || "", description: row.descricao || "", amount: parseCurrency(row.valor), date: row.data || new Date().toISOString(), due_date: row.vencimento || row.data || new Date().toISOString(), status: row.status || "pendente", type: row.tipo || "entrada", attachment_url: row.anexo || "", category: row.categoria || "" };
  if (table === "propostas") return { client_name: row.cliente || "", client_phone: row.telefone || "", work_type: row.servico || "outro", word_count: 1000, deadline_days: parseInt(row.prazo) || 7, price: parseCurrency(row.valor), service_level: "revisao_completa", extra_notes: "", client_message: row.mensagem_cliente || "", proposal_details: row.detalhamento || "" };
  return row;
}

function mapFromBackend(table, row) {
  if (table === "leads") return { ...row, nome: row.name, telefone: row.phone, valor_estimado: row.value, origem: row.source, notas: row.notes };
  if (table === "projetos") {
    const frontendStatus = STATUS_TO_FRONTEND[row.status] || row.status || "em andamento";
    // Extrair só a parte da data (YYYY-MM-DD) do prazo ISO
    const prazo = row.deadline ? row.deadline.substring(0, 10) : "";
    return { ...row, cliente: row.client_name, servico: row.work_type, tipo: row.work_type, prazo: prazo, valor: row.price, notas: row.notes, status: frontendStatus, tasks: row.tasks || [] };
  }
  if (table === "financeiro") {
    const dateStr = row.date ? row.date : (row.created_at ? row.created_at : (row.due_date ? row.due_date : ""));
    return { ...row, cliente: row.client_name, descricao: row.description, valor: row.amount, data: dateStr ? dateStr.substring(0, 10) : "", vencimento: row.due_date, tipo: row.type || "entrada", anexo: row.attachment_url || "", categoria: row.category || "" };
  }
  if (table === "propostas") return { ...row, cliente: row.client_name, telefone: row.client_phone || "", servico: row.work_type, prazo: row.deadline_days ? `${row.deadline_days} dias` : "", valor: row.price, mensagem_cliente: row.client_message || "", detalhamento: row.proposal_details || "" };
  return row;
}

export const db = {
  async select(table, { order, ascending = false, limit, eq } = {}) {
    const endpoint = TABLE_MAP[table] || table;
    let data = await doFetch(`${URL_}/${endpoint}`, { method: "GET", headers: authHeaders() });
    
    if (eq && Array.isArray(data)) {
      for (const [k, v] of Object.entries(eq)) {
        if (k !== 'user_id') data = data.filter(item => item[k] === v);
      }
    }
    
    if (order && Array.isArray(data)) {
      data.sort((a, b) => {
        if (a[order] < b[order]) return ascending ? -1 : 1;
        if (a[order] > b[order]) return ascending ? 1 : -1;
        return 0;
      });
    }
    
    if (limit && Array.isArray(data)) {
      data = data.slice(0, limit);
    }
    return Array.isArray(data) ? data.map(r => mapFromBackend(table, r)) : data;
  },
  async insert(table, row) {
    const endpoint = TABLE_MAP[table] || table;
    const backendRow = mapToBackend(table, row);
    const res = await doFetch(`${URL_}/${endpoint}`, {
      method: "POST", headers: authHeaders(), body: JSON.stringify(backendRow)
    });
    return mapFromBackend(table, res);
  },
  async update(table, id, patch) {
    const endpoint = TABLE_MAP[table] || table;
    let patchData = { ...patch };
    
    // Mapeamento para projetos
    if (table === "projetos") {
      const mapped = {};
      if (patch.cliente !== undefined) mapped.client_name = patch.cliente;
      if (patch.servico !== undefined) mapped.work_type = patch.servico;
      if (patch.valor !== undefined) mapped.price = Number(String(patch.valor).replace(/\./g, '').replace(',', '.'));
      if (patch.prazo !== undefined) {
        let d = patch.prazo;
        if (d && d.length === 10) d = d + "T00:00:00.000Z";
        mapped.deadline = d;
      }
      if (patch.status !== undefined) mapped.status = STATUS_TO_BACKEND[patch.status] || patch.status;
      if (patch.tasks !== undefined) mapped.tasks = patch.tasks;
      if (patch.notes !== undefined) mapped.notes = patch.notes;
      if (patch.revisor_id !== undefined) mapped.revisor_id = patch.revisor_id === "nenhum" ? "" : patch.revisor_id;
      if (patch.file_url !== undefined) mapped.file_url = patch.file_url;
      if (patch.delivery_url !== undefined) mapped.delivery_url = patch.delivery_url;
      if (patch.revisor_comments !== undefined) mapped.revisor_comments = patch.revisor_comments;
      patchData = mapped;
    }
    // Mapeamento para financeiro
    else if (table === "financeiro") {
      const mapped = {};
      if (patch.cliente !== undefined) mapped.client_name = patch.cliente;
      if (patch.descricao !== undefined) mapped.description = patch.descricao;
      if (patch.valor !== undefined) mapped.amount = parseCurrency(patch.valor);
      if (patch.vencimento !== undefined) mapped.due_date = patch.vencimento;
      if (patch.data !== undefined) mapped.date = patch.data;
      if (patch.status !== undefined) mapped.status = patch.status;
      if (patch.tipo !== undefined) mapped.type = patch.tipo;
      if (patch.project_id !== undefined) mapped.project_id = patch.project_id;
      if (patch.anexo !== undefined) mapped.attachment_url = patch.anexo;
      if (patch.categoria !== undefined) mapped.category = patch.categoria;
      patchData = mapped;
    }
    // Mapeamento simples para outras tabelas se necessário
    else if (table === "propostas") {
      const mapped = {};
      if (patch.cliente !== undefined) mapped.client_name = patch.cliente;
      if (patch.telefone !== undefined) mapped.client_phone = patch.telefone;
      if (patch.servico !== undefined) mapped.work_type = patch.servico;
      if (patch.valor !== undefined) mapped.price = parseCurrency(patch.valor);
      if (patch.prazo !== undefined) mapped.deadline_days = parseInt(patch.prazo);
      if (patch.status !== undefined) mapped.status = patch.status;
      if (patch.mensagem_cliente !== undefined) mapped.client_message = patch.mensagem_cliente;
      if (patch.detalhamento !== undefined) mapped.proposal_details = patch.detalhamento;
      patchData = mapped;
    }
    else if (table === "leads") {
      const mapped = {};
      if (patch.nome !== undefined) mapped.name = patch.nome;
      if (patch.email !== undefined) mapped.email = patch.email;
      if (patch.telefone !== undefined) mapped.phone = patch.telefone;
      if (patch.status !== undefined) mapped.status = patch.status;
      if (patch.valor_estimado !== undefined) mapped.value = parseCurrency(patch.valor_estimado);
      if (patch.origem !== undefined) mapped.source = patch.origem;
      if (patch.notas !== undefined) mapped.notes = patch.notas;
      patchData = mapped;
    }

    const res = await doFetch(`${URL_}/${endpoint}/${id}`, {
      method: "PATCH", headers: authHeaders(), body: JSON.stringify(patchData)
    });
    return mapFromBackend(table, res);
  },
  async remove(table, id) {
    const endpoint = TABLE_MAP[table] || table;
    return doFetch(`${URL_}/${endpoint}/${id}`, {
      method: "DELETE", headers: authHeaders()
    });
  },
  async publicSelectOne(table, id) {
    const endpoint = TABLE_MAP[table] || table;
    try {
      const res = await doFetch(`${URL_}/public/${endpoint}/${id}`, { method: "GET" });
      return mapFromBackend(table, res);
    } catch {
      return null;
    }
  },
  async publicUpdateStatus(table, id, status) {
    const endpoint = TABLE_MAP[table] || table;
    const res = await doFetch(`${URL_}/public/${endpoint}/${id}/status`, {
      method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ status })
    });
    return mapFromBackend(table, res);
  },
  async publicSignProposal(id, signatureData) {
    return doFetch(`${URL_}/public/proposals/${id}/sign`, {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(signatureData)
    });
  },
  
  // Custom Methods
  async sendProposalEmail(id, client_email) {
    return doFetch(`${URL_}/proposals/${id}/send`, {
      method: "POST", headers: authHeaders(), body: JSON.stringify({ client_email })
    });
  },
  async generateSpinSelling(client_message, work_type) {
    return doFetch(`${URL_}/proposals/generate-spin`, {
      method: "POST", headers: authHeaders(), body: JSON.stringify({ client_message, work_type })
    });
  },

  // Admin Methods
  async adminListUsers() {
    return doFetch(`${URL_}/admin/users`, { method: "GET", headers: authHeaders() });
  },
  async adminUpdateUser(id, patch) {
    return doFetch(`${URL_}/admin/users/${id}`, {
      method: "PATCH", headers: authHeaders(), body: JSON.stringify(patch)
    });
  },
  async adminDeleteUser(id) {
    return doFetch(`${URL_}/admin/users/${id}`, { method: "DELETE", headers: authHeaders() });
  },
  async requestPasswordReset(email) {
    return doFetch(`${URL_}/auth/forgot-password`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email })
    });
  },
  async resetPassword(token, password) {
    return doFetch(`${URL_}/auth/reset-password`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token, password })
    });
  },

  // Payment Methods
  async cancelSubscription() {
    return doFetch(`${URL_}/payments/cancel`, {
      method: "POST", headers: authHeaders()
    });
  },
  async createPaymentPreference(plan_key) {
    return doFetch(`${URL_}/payments/create-preference`, {
      method: "POST", headers: authHeaders(), body: JSON.stringify({ plan_key })
    });
  }
};


